import { createContext, useContext, useEffect, useState } from "react";
import { User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { useZombieSessionHandler } from "@/hooks/useZombieSessionHandler";
import { toast } from "sonner";

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  isLoading: true,
  signOut: async () => {},
});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  
  const signOut = async () => {
    try {
      console.log('🚪 [AuthProvider] Signing out...');
      const { error } = await supabase.auth.signOut();
      if (error) {
        console.error('❌ [AuthProvider] Error signing out:', error);
        toast.error('Failed to sign out. Please try again.');
      } else {
        console.log('✅ [AuthProvider] Successfully signed out');
        setUser(null);
        toast.success('Signed out successfully');
        // Navigate to login page after successful sign out
        window.location.href = '/auth/login';
      }
    } catch (error) {
      console.error('❌ [AuthProvider] Unexpected error during sign out:', error);
      toast.error('An unexpected error occurred. Please try again.');
    }
  };
  
  // Global zombie session monitoring
  const { parseAuthError } = useZombieSessionHandler({
    onBanDetected: (details) => {
      console.error('🚫 [AuthProvider] Banned user detected:', details);
      toast.error(details.reason === 'suspended' 
        ? `Account suspended until ${new Date(details.until!).toLocaleDateString()}`
        : 'Your account has been banned. Please contact support.'
      );
      setUser(null);
    },
    onZombieDetected: () => {
      console.warn('🧟 [AuthProvider] Global zombie session detected');
    }
  });

  useEffect(() => {
    console.log('🔐 [AuthProvider] Initializing auth provider...');
    
    // Get initial session
    supabase.auth.getSession().then(({ data: { session }, error }) => {
      if (error) {
        console.error('❌ [AuthProvider] Error getting initial session:', error);
        setIsLoading(false); // Important: Set loading to false even on error
        return;
      }
      console.log('🔍 [AuthProvider] Initial session check:', {
        hasSession: !!session,
        userId: session?.user?.id,
        email: session?.user?.email,
        provider: session?.user?.app_metadata?.provider
      });
      setUser(session?.user ?? null);
      setIsLoading(false);
    }).catch((err) => {
      console.error('❌ [AuthProvider] Unexpected error getting session:', err);
      setIsLoading(false); // Ensure loading is set to false on any error
    });

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      console.log('🔄 [AuthProvider] Auth state changed:', {
        event,
        hasSession: !!session,
        userId: session?.user?.id,
        email: session?.user?.email,
        provider: session?.user?.app_metadata?.provider
      });
      
      // CRITICAL: Set user and loading state immediately to prevent deadlock
      setUser(session?.user ?? null);
      setIsLoading(false);
      
      // CRITICAL: Use setTimeout to defer async operations and prevent deadlock
      // This is a known Supabase issue with async callbacks in onAuthStateChange
      setTimeout(async () => {
        // Handle OAuth profile creation for new users
        if (event === 'SIGNED_IN' && session?.user) {
          const provider = session.user.app_metadata?.provider || 'email';
          
          // Create profile for ALL users (OAuth and email/password)
          console.log('🔧 [AuthProvider] User sign-in detected, checking profile...', { provider, userId: session.user.id });
          
          // Check if profile exists
          const { data: existingProfile, error: checkError } = await supabase
            .from('profiles')
            .select('user_id')
            .eq('user_id', session.user.id)
            .maybeSingle();
          
          if (checkError && checkError.code !== 'PGRST116') {
            console.error('❌ [AuthProvider] Error checking profile:', checkError);
          }
          
          if (!existingProfile) {
            console.log('📝 [AuthProvider] Creating profile for user...');
            
            const profileData = {
              user_id: session.user.id,
              display_name: session.user.user_metadata?.full_name || 
                           session.user.user_metadata?.name ||
                           session.user.user_metadata?.username ||
                           session.user.email?.split('@')[0] || 
                           'User',
              email: session.user.email,
              email_verified: !!session.user.email_confirmed_at,
              avatar_url: session.user.user_metadata?.avatar_url || 
                         session.user.user_metadata?.picture || 
                         null,
              avatar_color: '#3B82F6', // Default avatar color
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString()
            };
            
            console.log('📝 [AuthProvider] Profile data:', profileData);
            
            const { error: createError } = await supabase
              .from('profiles')
              .insert(profileData);
            
            if (createError) {
              console.error('❌ [AuthProvider] Profile creation failed:', createError);
              
              // Try RPC fallback
              console.log('📝 [AuthProvider] Trying RPC fallback...');
              const { error: rpcError } = await supabase.rpc('create_my_profile');
              
              if (rpcError) {
                console.error('❌ [AuthProvider] RPC fallback also failed:', rpcError);
              } else {
                console.log('✅ [AuthProvider] Profile created via RPC');
              }
            } else {
              console.log('✅ [AuthProvider] Profile created successfully');
            }
          } else {
            console.log('✅ [AuthProvider] Profile already exists');
          }
        }
      }, 0); // Defer to next tick to prevent deadlock
    });

    return () => subscription.unsubscribe();
  }, []);

  return (
    <AuthContext.Provider value={{ user, isLoading, signOut }}>
      {children}
    </AuthContext.Provider>
  );
};
